import pickBy from "lodash-es/pickBy";
import React from "react";
import { Channel, EventChannel, channel, eventChannel } from "redux-saga";
import { all, call, delay, fork, put, race, select, take, takeEvery } from "redux-saga/effects";

import { compareForShowOnThisDevice } from "@/mk2/containers/AdSlot/AdSlot";
import {
    AD_SLOT_REFRESH,
    AD_SLOT_REGISTER,
    AD_SLOT_UNREGISTER,
    AdSlotRefreshAction,
    AdSlotRegisterAction,
    AdSlotUnregisterAction,
    adSlotRenderEnded,
} from "@/mk2/containers/AdSlot/AdSlot.actions";
import { dbg, isInterstitialId, withGpt } from "@/mk2/containers/AdSlot/AdSlot.helpers";
import { getLogger } from "@/mk2/logger";
import {
    AppState,
    getRequestDeviceMobile,
    getRequestUserAdTargeting,
    getRequestUserHashedEmail,
    getRequestUserHashedId,
} from "@/mk2/reducers";
import { AD_UNITS, AdUnit, Collapse } from "@/mk/ox/hbAdUnits";

// !! Read/write these variables only inside withGpt(...)
// It acts as a mutex for accessing them.
//
// Otherwise, there's a risk of race conditions if other
// withGpt(...) calls, which might change these variables,
// are still waiting in the queue to be executed.
const registeredSlots = new Map<string, googletag.Slot>();
const slotsToRefresh = new Set<string>();

const logger = getLogger("AdSlot");

let googletag;

declare const window;

// Message structure for the render-ended channel.
interface RenderEndedMessage {
    slotId: string;
    event: { isEmpty: boolean; size: string | number[] };
}

// Channel that collects ad slot refresh requests.
// These are batched into a single googletag.pubads().refresh() call.
interface RefreshRequestedMessage {}
let refreshRequestedChannel: Channel<RefreshRequestedMessage> | null = null;

// During initialization (where enableServices is called).
// (Assumes this runs inside withGpt(...))
function bindGptEvents() {
    googletag
        .pubads()
        .addEventListener("slotRequested", (e: googletag.events.SlotRequestedEvent) => {
            dbg("[ads:dbg] admanager:slotRequested", {
                slotId: e.slot.getSlotElementId(),
                adUnit: e.slot.getAdUnitPath(),
            });
        });

    googletag
        .pubads()
        .addEventListener("slotResponseReceived", (e: googletag.events.SlotResponseReceived) => {
            dbg("[ads:dbg] admanager:slotResponseReceived", {
                slotId: e.slot.getSlotElementId(),
                adUnit: e.slot.getAdUnitPath(),
            });
        });

    googletag
        .pubads()
        .addEventListener("slotRenderEnded", (e: googletag.events.SlotRenderEndedEvent) => {
            dbg("[ads:dbg] admanager:slotRenderEnded", {
                slotId: e.slot.getSlotElementId(),
                adUnit: e.slot.getAdUnitPath(),
                isEmpty: e.isEmpty,
                lineItemId: e.lineItemId,
                creativeId: e.creativeId,
                advertiserId: e.advertiserId,
            });
        });
}

function* initializeAdSystem() {
    dbg("[ads:dbg] initializeAdSystem:start");

    const [hashedId, hashedEmail] = yield all([
        select(getRequestUserHashedId),
        select(getRequestUserHashedEmail),
        // select(getRequestUserHashedPD),
    ]);
    const interstitialPreview = Boolean(
        window.location?.search?.includes?.("__interstitial_preview=true"),
    );

    googletag = window.googletag = window.googletag || { cmd: [] };

    // Following the example from:
    // https://developers.google.com/publisher-tag/samples/lazy-loading?hl=en#typescript
    withGpt(() => {
        googletag.setConfig({
            // Pause ad requests and wait for the user to interact with
            // a consent request UI before issuing ad requests.
            // https://support.google.com/adsense/answer/7670312?hl=en
            disableInitialLoad: true,
            singleRequest: true,
            collapseDiv: "ON_NO_FILL",
            targeting: pickBy(
                {
                    mk_dev_key: interstitialPreview ? "preview" : undefined,
                    email_hash: hashedEmail,
                    user_id_hash: hashedId,
                },
                (v) => !!v,
            ), // Filter out undefined/null values
        });

        bindGptEvents();
        // setupTcfListenerOnce();
    });

    // Create and run a channel to forward render-ended events
    // (SlotRenderEndedEvent) from AdManager to React components.
    yield fork(runEmitRenderEndedLoop);

    // Register all slots already rendered into HTML on the server
    // (i.e., call googletag.defineSlot() for them).
    yield call(registerAdSlotsFromServer);

    // Finalize googletag initialization.
    //
    // Since disableInitialLoad is true, this won't fetch ads yet.
    // Fetching will wait for googletag.pubads().refresh().
    withGpt(() => {
        dbg("[ads:dbg] admanager:enableGptServices");
        googletag.enableServices();
    });

    yield fork(runEmitRefreshAdsLoop);
    dbg("[ads:dbg] initializeAdSystem:end");
}

function* registerAdSlotsFromServer() {
    const slots = yield select((state: AppState) => state.response.registeredSlots);
    const isMobile = yield select(getRequestDeviceMobile);

    for (const key of Object.keys(slots)) {
        const slot = slots[key];
        if (compareForShowOnThisDevice(isMobile, slot.zone)) {
            yield call(registerAdSlot, slot);
        }
    }
}

function buildGptSizeMapping(adUnit: AdUnit): {
    mapping: googletag.SizeMappingArray;
    allSizes: googletag.SingleSize[];
} {
    let b = googletag.sizeMapping();
    const allSizes: googletag.SingleSize[] = [];
    adUnit.sizeConfig.forEach((sc) => {
        const screen = [sc.minScreenWidth, 0] as [number, number];
        const sizes: googletag.SingleSize[] = [...sc.bannerSizes];
        if (sc.native) sizes.push("fluid");
        allSizes.push(...sizes);
        b = b.addSize(screen, sizes);
    });
    return { mapping: b.build(), allSizes };
}

function defineGptSlot(
    adUnit: AdUnit,
    slotId: string,
    allSizes: googletag.SingleSize[],
    mapping: googletag.SizeMappingArray,
): googletag.Slot {
    const isInterstitial = isInterstitialId(slotId);
    const gSlot: googletag.Slot = !isInterstitial
        ? googletag.defineSlot(adUnit.gptid, allSizes, slotId)
        : googletag.defineOutOfPageSlot(adUnit.gptid, googletag.enums.OutOfPageFormat.INTERSTITIAL);

    if (gSlot && !isInterstitial) gSlot.defineSizeMapping(mapping);
    if (gSlot) gSlot.addService(googletag.pubads());

    if (gSlot && isInterstitial) {
        gSlot.setConfig({
            interstitial: {
                triggers: { navBar: false, unhideWindow: false },
            },
        });
    }
    return gSlot;
}

function applySlotTargeting(
    gSlot: googletag.Slot,
    targeting?: Record<string, string | string[]>,
    userTargeting?: Record<string, string | string[]>,
) {
    // dbg('[ads:dbg] applySlotTargeting', {
    //      hasGSlot: !!gSlot,
    //      targetingLen: targeting ? Object.keys(targeting).length : 0,
    //      userTargetingLen: userTargeting ? Object.keys(userTargeting).length : 0,
    // });

    gSlot.setConfig({
        targeting: pickBy(
            {
                ...targeting,
                // ...userTargeting,
            },
            (v) => !!v,
        ),
    });
}

function applySlotCollapse(gSlot: googletag.Slot, adUnit: AdUnit) {
    if (!gSlot || adUnit.collapse === undefined) return;
    const collapseModes: Record<Collapse, googletag.config.CollapseDivBehavior> = {
        [Collapse.NEVER]: "DISABLED",
        [Collapse.AFTER_FETCH]: "ON_NO_FILL",
        [Collapse.BEFORE_FETCH]: "BEFORE_FETCH",
    };
    gSlot.setConfig({
        collapseDiv: collapseModes[adUnit.collapse],
    });
}

function* registerAdSlot({ slotId, zone, targeting }: AdSlotRegisterAction) {
    const userTargeting = yield select(getRequestUserAdTargeting);

    withGpt(() => {
        if (registeredSlots.has(slotId)) {
            // Slot might already be registered (e.g., from server-side rendering),
            // and now we're trying to register it again from the client side.
            dbg("[ads:dbg] register:skip", {
                slotId,
                zone,
                registeredSlots: registeredSlots.keys(),
            });
            return;
        }
        dbg("[ads:dbg] register:start", { slotId, zone });

        const adUnit: AdUnit = AD_UNITS[zone];
        const { mapping, allSizes } = buildGptSizeMapping(adUnit);
        const gSlot = defineGptSlot(adUnit, slotId, allSizes, mapping);
        applySlotTargeting(gSlot, targeting, userTargeting);
        applySlotCollapse(gSlot!, adUnit);

        // With disabled initial load, calls to display() will only register the ad slot.
        // No ad content will be loaded until a second action is taken. This lets you
        // precisely control when ad requests are made.
        // https://developers.google.com/publisher-tag/guides/control-ad-loading?hl=en
        googletag.display(gSlot);

        registeredSlots.set(slotId, gSlot);
        dbg("[ads:dbg] register:end", { slotId, adUnit: gSlot.getAdUnitPath(), gSlot });

        dbg("[ads:dbg] refresh:request", { slotId, adUnit: gSlot.getAdUnitPath(), gSlot });
        slotsToRefresh.add(slotId); // FIXME: Should this be called for interstitials on their first registration?
        refreshRequestedChannel.put({});
    });
}

function* unregisterAdSlot({ slotId }: AdSlotUnregisterAction) {
    withGpt(() => {
        const gSlot = registeredSlots.get(slotId);
        if (!gSlot) {
            // Slot isn't registered.
            dbg("[ads:dbg] unregister:skip", { slotId, registeredSlots: registeredSlots.keys() });
            return;
        }

        dbg("[ads:dbg] unregister:start", { slotId, adUnit: gSlot.getAdUnitPath(), gSlot });
        googletag.destroySlots([gSlot]);
        registeredSlots.delete(slotId);
        slotsToRefresh.delete(slotId);
    });
}

function* refreshAdSlot({ slotId }: AdSlotRefreshAction) {
    if (isInterstitialId(slotId)) {
        dbg("[ads:dbg] refresh:skip (interstitial)", { slotId });
        return;
    }

    withGpt(() => {
        const gSlot = registeredSlots.get(slotId);
        if (!gSlot) {
            dbg("[ads:dbg] refresh:skip (unregistered slot)", { slotId });
            return;
        }

        dbg("[ads:dbg] refresh:request", { slotId, adUnit: gSlot.getAdUnitPath(), gSlot });
        slotsToRefresh.add(slotId);
        refreshRequestedChannel.put({});
    });
}

function createRenderEndedChannel(): EventChannel<RenderEndedMessage> {
    // Listen for the event from AdManager and forward it via a Redux-Saga channel
    // to the React components.
    const renderEndedChannel = eventChannel<RenderEndedMessage>((emitter) => {
        const handler = (event: googletag.events.SlotRenderEndedEvent) => {
            if (!event.slot) {
                // This handler sometimes catches events from `postMessage` that lack a `slot` property.
                return; // Ignore these events.
            }
            const slotId = event.slot.getSlotElementId();

            const msg: RenderEndedMessage = {
                slotId,
                event: { isEmpty: event.isEmpty, size: event.size },
            };
            emitter(msg);
        };

        // Subscribe
        withGpt(() => {
            googletag.pubads().addEventListener("slotRenderEnded", handler);
        });

        return () => {
            // Unsubscribe when the channel is destroyed.
            withGpt(() => {
                googletag.pubads().removeEventListener("slotRenderEnded", handler);
            });
        };
    });

    return renderEndedChannel;
}

// Forwards events from AdManager to React components
// about an ad being rendered in an ad slot.
//
// This allows components to respond (e.g., the branding slot
// adjusts global CSS based on the creative size,
// like 2000x1400 branding or 970x250 billboard).
function* runEmitRenderEndedLoop() {
    const renderEndedChannel: EventChannel<RenderEndedMessage> = createRenderEndedChannel();

    try {
        while (true) {
            const { slotId, event } = yield take(renderEndedChannel);
            yield put(adSlotRenderEnded(slotId, event));
        }
    } catch (err) {
        logger.error(err);
    }
}

function waitForGdprConsent(): Promise<void> {
    return new Promise((resolve) => {
        window.googlefc = window.googlefc || {};
        window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];
        window.googlefc.callbackQueue.push({
            CONSENT_DATA_READY: () =>
                (window as any).__tcfapi("addEventListener", 2, (data, success) => {
                    dbg("[ads:dbg] consent:ready", { data, success });
                    // User has made a choice, or a saved consent state was loaded.
                    // This can be called multiple times if user consent selections change.
                    resolve();
                }),
        });
    });
}

function* runEmitRefreshAdsLoop() {
    dbg("[ads:dbg] refreshLoop:start");

    // On start, wait for GDPR consent or timeout after 2 seconds.
    //
    // This just needs to wait for the saved consent state to load.
    // If the consent dialog is open, the refresh() issued after
    // the 2-second timeout will be queued by googletag until the
    // dialog is closed.
    const { consent, expired } = yield race({
        consent: call(waitForGdprConsent),
        expired: delay(2000),
    });
    dbg("[ads:dbg] refreshLoop:consent", { consent: !!consent, expired: !!expired });

    // https://redux-saga.js.org/docs/api#debouncems-pattern-saga-args
    // Debounce, but don't run as a Redux action to avoid the Redux
    // event loop. This prevents normal UI code from delaying the ad refresh.
    while (true) {
        let refreshRequest = yield take(refreshRequestedChannel);
        dbg("[ads:dbg] refreshLoop:wakeup");

        while (true) {
            // Wait 50ms for other ad refresh requests
            const { debounced, newRefreshRequest } = yield race({
                debounced: delay(50),
                newRefreshRequest: take(refreshRequestedChannel),
            });
            dbg("[ads:dbg] refreshLoop:debounce", {
                debounced: !!debounced,
                newRefreshRequest: !!newRefreshRequest,
            });

            // No new ad refresh request during the debounce period.
            // Run the refresh now.
            if (debounced) {
                withGpt(() => {
                    const refreshIds = Array.from(slotsToRefresh);
                    const gSlots: googletag.Slot[] = Array.from(slotsToRefresh).map((slotId) =>
                        registeredSlots.get(slotId),
                    );
                    // .filter(Boolean) as googletag.Slot[];
                    dbg("[ads:dbg] refreshLoop:refresh", {
                        slotIds: refreshIds.join(", "),
                        adUnits: gSlots.map((s) => s.getAdUnitPath()).join(", "),
                        gSlots,
                    });

                    if (gSlots.length === 0) {
                        return;
                    }

                    googletag.pubads().refresh(gSlots);
                    slotsToRefresh.clear();
                });

                break;
            }

            refreshRequest = newRefreshRequest;
        }
    }
}

export default function* root() {
    if (!process.env.SERVER) {
        if (window.location?.search?.includes?.("__disable_ads=true")) {
            return null;
        }

        // Create the refresh request channel before initializeAdSystem,
        // registerAdSlot, or refreshAdSlot try to use it.
        //
        // Their execution order isn't guaranteed, so we can't
        // safely create the channel inside initializeAdSystem.
        refreshRequestedChannel = channel();

        // Initialize AdManager, start processing commands from React,
        // and run the ad refresh loop.
        yield fork(initializeAdSystem);
    }

    // Listen for commands from React (UI).
    yield all([
        takeEvery(AD_SLOT_REGISTER, registerAdSlot),
        takeEvery(AD_SLOT_UNREGISTER, unregisterAdSlot),
        takeEvery(AD_SLOT_REFRESH, refreshAdSlot),
    ]);
}
